import { useEffect, useMemo } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetPerformanceTestsHistoryQuery } from "store/api/performanceTestApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDataToPerformanceTestHistoryTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"

import { dummyMeta, dummyPerfTestDevices } from "../PerformanceTestPage/dummyPerfTestData"
import PerformanceTestHistoryPageContent from "./PerformanceTestHistoryPageContent"
import performanceTestHistoryTableHeaderCells from "./performanceTestHistoryTableHeaderCells"

const PerformanceTestHistoryPage = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [triggerGetPerformanceTestsHistory, { isFetching, data }] = useLazyGetPerformanceTestsHistoryQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const requestParams = {
    orderColumn: "time",
    orderType: "DESC" as UITypes.Sort["orderType"],
    ...Object.fromEntries(Object.entries(urlQueryParams).filter(([key]) => !["time_start", "time_end"].includes(key))),
  }
  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false
  const meta = hasPerformanceTestAccess ? data?.meta : dummyMeta
  const isFetchingData = isFetching && !data
  const devices = useMemo(() => {
    if (!hasPerformanceTestAccess) {
      return dummyPerfTestDevices
    }

    return data?.data || []
  }, [data, hasPerformanceTestAccess])

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_performance_test_history_list" })
  }, [])

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(performanceTestHistoryTableHeaderCells, columnsData?.data?.config)
        : performanceTestHistoryTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: requestParams,
    headerCells: tableHeaderCells,
    meta: meta,
    fileName: "performance_test_history",
    convertData: convertDataToPerformanceTestHistoryTableRows,
    useLazyQuery: useLazyGetPerformanceTestsHistoryQuery,
  })

  useEffect(() => {
    sendRequest(requestParams)
  }, [urlQueryParams])

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetPerformanceTestsHistory(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <PerformanceTestHistoryPageContent
        devices={devices}
        meta={meta}
        isFetching={isFetchingData}
        onExport={handleExport}
        tableHeaderCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        urlQueryParams={urlQueryParams}
      />
      {!hasPerformanceTestAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.PerformanceTest} />}
    </>
  )
}

export default PerformanceTestHistoryPage
